// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../../src/environment.d.ts" />

import { Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IdentityApiService } from '@api_patient/identity_api.service';
import { AuthService } from '@application/authentication/auth.service';
import { IdpUrlRedirectStorageService } from '@public/src/application/idp_url_redirect_storage.service';
import { LicenceHolderService } from '@services/licence_holder.service';
import { LocationSelectorService } from '@services/location_selector.service';
import { sharedResolution } from '../../src/shared_resolution';
import { PageLoadHandler } from './Classes/PageLoadHandler';
import { SpaContext } from './Classes/spacontext';
import { SpaLogger } from './Common/Classes/Global/spalogger.class';
import { visibaProgressBar } from './Common/Directives/visibaProgressBar';
import { Helper } from './Common/Helpers/helper';
import { OpenTargetWith } from './Components/Authentication/signin.component';
import { NotificationComplianceComponent } from './Components/notificationCompliance.component';
import { IdentityServiceV2 } from './Services/api/v2/identity.service';
import { IdentityApiModel } from './Services/api/v2/Models/IdentityApiModel';
import { NotificationSettingsService } from './Services/api/v2/notificationSettings.service';
import { UsageAgreementsService } from './Services/api/v2/usageAgreements.service';
import { StartupService } from './Services/app/startup.service';
import { ApplicationService } from './Services/application.service';
import { AuthContextService } from './Services/authContext.service';
import { GeneralService } from './Services/general.service';
import { SignatureDocuments } from './Services/Models/Client/SignatureDocuments';
import { ModalService } from './Services/modalService';
import { SpaEventService } from './Services/spaEvents.service';

@Component({
	standalone: false,
	selector: 'app-root',
	templateUrl: './app.component.html',
	encapsulation: ViewEncapsulation.None,
	styleUrls: ['./app.component.scss'],
	host: { '[class]': 'currentPageClassName' },
})
export class AppComponent {
	browserIsSupported: boolean;
	versionIsSupported = true;
	signIn = {
		target: null,
		reason: null,
		ticket: null,
		leave: null,
		authMethod: null,
		openTargetWith: null,
	};
	registration: { target: string; ticket: string } | null = null;
	signatures: { documents: SignatureDocuments } | null = null;
	currentPage = '';
	currentPageClassName = '';
	headerHeight = 40;
	@ViewChild('headerComp', { static: false }) headerComp: ElementRef;
	@ViewChild('loader', { static: false }) loader: visibaProgressBar;
	identityIntervalOutsider: number = null;
	identityInterval: number = null;
	private requireLogin = true;

	public readonly licenceHolderService = sharedResolution.resolve(LicenceHolderService);
	public readonly locationSelectorService = sharedResolution.resolve(LocationSelectorService);
	public readonly identityApiService = sharedResolution.resolve(IdentityApiService);
	public readonly authService = sharedResolution.resolve(AuthService);

	constructor(
		public spaContext: SpaContext,
		public router: Router,
		public helper: Helper,
		public authContextService: AuthContextService,
		private spaLogger: SpaLogger,
		private startupService: StartupService,
		private spaEventService: SpaEventService,
		private identityService: IdentityServiceV2,
		private pageLoadHandler: PageLoadHandler,
		private modalService: ModalService,
		private notificationSettingsService: NotificationSettingsService,
		private applicationService: ApplicationService,
		private usageAgreementsServie: UsageAgreementsService,
		private generalService: GeneralService,
	) {
		//#region Event listeners.

		window.onerror = (
			errorMsg: unknown,
			url: string,
			lineNumber: number,
			colNumber: number,
			error: { stack?: string; name?: string } = null,
		) => {
			if (typeof error !== 'undefined' && error !== null) {
				this.spaLogger.log(
					'Debug',
					['Public', 'ConsoleError'],
					`${error.name}: ${error['message']}`,
					{
						Url: window.location.href,
						LicensHolderId: this.spaContext.unitConfig.LicenceHolder.Id,
						UnitId: this.spaContext.unitConfig.Unit.UnitId,
						ErrorType: error.name,
					},
					error.stack,
				);
			}
		};

		window.addEventListener('triggerSignIn', (event: CustomEvent) => {
			this.spaEventService.onSignInRequired.emit(event.detail);
		});

		this.spaEventService.onSignInRequired.subscribe((event) => {
			this.signIn.target = event.target;
			this.signIn.ticket = typeof event.callticket === 'undefined' ? null : event.callticket;
			this.signIn.reason = typeof event.reason === 'undefined' ? null : event.reason;
			this.signIn.leave = typeof event.leave === 'undefined' ? true : event.leave;
			this.signIn.authMethod = typeof event.authMethod === 'undefined' ? null : event.authMethod;
			this.signIn.openTargetWith =
				typeof event.openTargetWith === 'undefined' ? OpenTargetWith.AngularNavigation : event.openTargetWith;

			if (__visiba_feature_flags__['CareExperience.PW.Idp']) {
				sharedResolution.resolve(IdpUrlRedirectStorageService).setRedirectPath(this.signIn.target);
			}
		});

		this.spaEventService.onSignedOut.subscribe((value) => {
			this.visitorSignedOut(value?.redirectUrl);
		});

		this.spaEventService.onChangeUnit.subscribe((data) => {
			this.changeUnit(data.unitId);
		});

		this.spaEventService.onSignedOutAsOutsider.subscribe((value) => {
			this.visitorSignedOutAsOutsider(null, value?.reloadAfterSignOut);
		});

		this.spaEventService.onSignInAborted.subscribe(() => {
			this.signIn.target = null;
			this.signIn.authMethod = null;
			this.signIn.openTargetWith = null;
			this.signIn.ticket = null;
			this.signIn.reason = null;

			window.dispatchEvent(new CustomEvent('abortSignIn'));
		});

		this.spaEventService.onSignedIn.subscribe((identity: IdentityApiModel) => {
			this.signIn.target = null;
			if (identity.UserType === 'Client') {
				this.checkNotificationCompliance();
			}
			this.visitorSignedIn();
		});
		this.spaEventService.onSignedInAsOutsider.subscribe((overrideRequireLogin) => {
			this.visitorSignedInAsOutsider();
			this.signIn.target = null;

			if (this.requireLogin || overrideRequireLogin) {
				this.checkIdentity();
			}
		});

		this.spaEventService.onRegistrationRequired.subscribe((data) => {
			this.signIn.target = null;
			this.registration = { target: data.target, ticket: data.ticket };
		});

		this.spaEventService.onNotificationComplianceNeeded.subscribe((settings) => {
			this.modalService.detach();
			const notificationComplianceComponent = this.modalService.attach(NotificationComplianceComponent, false);
			notificationComplianceComponent.instance.notificationSettings = settings;
		});

		this.spaEventService.onNotificationComplianceConfirmation.subscribe((settings) => {
			this.modalService.detach();
			const notificationComplianceComponent = this.modalService.attach(NotificationComplianceComponent, false);
			notificationComplianceComponent.instance.showConfirmationDialog = true;
			notificationComplianceComponent.instance.notificationSettings = settings;
			notificationComplianceComponent.instance.onCancel.subscribe(() => {
				this.modalService.detach();
				settings.onCancel();
			});
		});

		this.spaEventService.onNotificationComplianceCompleted.subscribe(() => {
			this.modalService.detach();
		});

		this.spaEventService.onRegistrationAborted.subscribe(() => {
			this.registration = null;
		});
		this.spaEventService.onVisitorRegistred.subscribe(() => {
			this.registration = null;
		});
		this.spaEventService.onDocumentsSigningNeeded.subscribe((documents) => {
			this.signatures = { documents };
		});
		this.spaEventService.onDocumentsSigningCompleted.subscribe((documents) => {
			this.signatures = null;
			this.spaContext.documentSigningPending = false;
		});
		this.spaEventService.onDocumentsSigningAborted.subscribe((documents) => {
			this.spaContext.documentSigningPending = false;
			this.signatures = null;
			this.registration = null;

			if (this.signIn.authMethod !== null) {
				const cachedTarget = this.signIn.target;
				setTimeout(() => {
					this.signIn.target = cachedTarget;
				}, 100);
			}
			this.signIn.target = null;
		});

		this.router.events.subscribe((e) => {
			if (e instanceof NavigationEnd) {
				let currentPage: string;

				const urlParts = e.urlAfterRedirects.replace(this.locationSelectorService.location.route, '').split('/');
				if (urlParts.every((value) => value === '')) {
					currentPage = 'index';
				} else {
					currentPage = urlParts.find((value) => value !== '');
				}

				this.currentPage = currentPage;
				this.currentPageClassName = 'area_' + currentPage;
			}
		});

		this.pageLoadHandler.event.subscribe((event) => {
			this.loader.show(event === 'hide' ? true : false);
		});

		//#endregion

		//#region Initialization

		this.spaContext.init(this.startupService.unitConfigData, this.startupService.unitTreeData);

		this.applicationService.webVersion = this.startupService.webVersion;

		this.authContextService.signInPatient(null);
		this.authContextService.resourceIdentity = this.startupService.resourceIdentityData;

		this.checkIdentity();

		if (µ.isDevelopment) this.spaLogger.logLevel = 3;

		//#endregion
	}

	checkNotificationCompliance() {
		this.notificationSettingsService.getComplianceSettings().subscribe((response) => {
			const currentSettings = response.data;
			if (!currentSettings.UpToDate) {
				this.spaEventService.onNotificationComplianceNeeded.emit(response.data);
				const complianceSubscription = this.spaEventService.onNotificationComplianceCompleted.subscribe((newSettings) => {
					complianceSubscription.unsubscribe();
				});
			}
		});
	}

	checkIdentity() {
		if (
			(this.authContextService.isPatient() || this.startupService.patientIdentityData !== null) &&
			this.startupService.resourceIdentityData == null
		) {
			if (__visiba_feature_flags__['CareExperience.PW.IDP']) {
				if (!this.authContextService.needsLegacyDocumentSigning()) {
					return;
				}
			}

			this.spaContext.documentSigningPending = true;
			this.usageAgreementsServie.getSignatureDocuments(this.spaContext.unitConfig.LicenceHolder.Id).subscribe(
				(response) => {
					this.requireLogin = false;

					if (!response.data.PrivacyPolicy.HasSigned || !response.data.TermsOfService.HasSigned) {
						this.spaEventService.onDocumentsSigningNeeded.emit(response.data);

						const signingSubscription = this.spaEventService.onDocumentsSigningCompleted.subscribe((documents) => {
							this.completeSignIn();
							signingSubscription.unsubscribe();
						});

						return;
					}
					this.completeSignIn();
					this.spaEventService.onDocumentsSigningCompleted.emit(); // Emit so authGuard can let navigation through
				},
				(error) => {
					// noop
				},
			);

			if (!this.authContextService.isOutsider) {
				this.checkNotificationCompliance();
			}
		}
	}

	completeSignIn() {
		if (this.authContextService.patientIdentity === null) {
			this.authContextService.signInPatient(null);
			this.startupService.patientIdentityData = null;
		}
	}

	visitorSignedIn() {
		this.identityInterval = window.setInterval(() => {
			this.generalService.isAlive().subscribe(
				(result) => {
					// If signin is in progress dont reset. Promted from HttpsClass when 401
					if (!result.ClientId && this.signIn.target === null) {
						this.visitorSignedOut(null);
					}

					// This is to catch the case when user is signed in as another user in another tab.
					// Then we want to reload the page to update the UI with the new users info.
					if (this.authService.getUser().id !== result.ClientId) {
						location.reload();
					}
				},
				(error) => {
					if (error.status === 401) {
						this.visitorSignedOut(null);
					}
				},
			);
		}, 30 * 1000);
	}

	visitorSignedOut(redirect: string | null) {
		clearInterval(this.identityInterval);

		const signOutPath = this.identityApiService.getSignOutPath(redirect ?? `${location.origin}/${this.licenceHolderService.alias}`);

		window.location.href = signOutPath;
	}

	visitorSignedInAsOutsider() {
		if (this.identityIntervalOutsider !== null) return;

		this.identityIntervalOutsider = window.setInterval(() => {
			this.identityService.isAliveOutsider().subscribe(
				(result) => {
					if (!result.data) this.visitorSignedOutAsOutsider(null);
				},
				(error) => {
					// noop
				},
			);
		}, 30 * 1000);
	}

	visitorSignedOutAsOutsider(redirect: string | null, reloadAfterSignOut = true) {
		clearInterval(this.identityInterval);
		if (redirect != null) {
			window.location.href = redirect;

			return;
		}

		if (reloadAfterSignOut) {
			window.location.href = `${location.origin}/${this.licenceHolderService.alias}`;
		}
	}

	private changeUnit(unitId: number) {
		this.spaContext.setGuiConfigItem('Selected_Unit', unitId);
		const url = this.helper.unit.getFullUrlAlias(unitId, this.spaContext.unitTreeFlat);
		window.location.href = url;

		// Cleaner reload some day?
		// Observable.forkJoin(
		//    this._unitService.getTree(),
		//    //this._unitService.getConfig(),
		//    //this._unitService.getAbout(),
		// )
		//    .subscribe(
		//    ([unitTree, unitConfig, about]) => {

		//        //unitConfig.data.About = about.data;
		//        //this.spaContext.unitConfig = unitConfig.data;
		//        //this._router.navigate([needsApproval ? '/approve' : '/_dev']);
		//    },
		// );
	}
}
